import React, { useState } from 'react';
import { Search, Loader2, X, ShieldAlert, Target, Zap, ArrowRight, ExternalLink, MessageCircle, AlertCircle, Globe } from 'lucide-react';

const CompetitionSpyModal = ({ isOpen, onClose, onModel }) => {
  const [url, setUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const handleSpy = async () => {
    if (!url) return;
    setIsAnalyzing(true);
    setError(null);
    setResults(null);

    try {
      const res = await fetch('/api/scout/spy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResults(data);
      }
    } catch (err) {
      setError('Failed to reach the Ninja Spy server. Check your connection.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-700/50 rounded-[3rem] w-full max-w-2xl shadow-[0_0_100px_rgba(244,63,94,0.15)] overflow-hidden relative flex flex-col transform animate-in zoom-in-95 duration-300 max-h-[90vh]">
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-rose-500 via-purple-500 to-rose-500"></div>
        
        <div className="p-10 flex flex-col h-full overflow-hidden">
          <div className="flex items-center justify-between mb-8 shrink-0">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-rose-600/20 text-rose-400 rounded-2xl border border-rose-500/20 shadow-inner">
                <Target size={28} />
              </div>
              <div>
                <h3 className="text-2xl font-black text-white tracking-tight uppercase">Competition Spy</h3>
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-[0.2em] mt-0.5">Reverse-Engineer Winning Funnels</p>
              </div>
            </div>
            <button onClick={onClose} className="p-3 text-slate-500 hover:text-white hover:bg-slate-800 rounded-2xl transition-all">
              <X size={24} />
            </button>
          </div>

          {!results ? (
            <div className="space-y-8 animate-in fade-in py-4">
              <div className="space-y-4">
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Competitor Landing Page URL</label>
                <div className="flex space-x-3">
                  <input 
                    type="url" 
                    value={url} 
                    onChange={e => setUrl(e.target.value)} 
                    placeholder="https://competitor-funnel.com/presell-page" 
                    className="flex-1 bg-slate-950/50 border border-slate-700/50 text-white px-6 py-4 rounded-2xl focus:outline-none focus:border-rose-500 focus:ring-1 focus:ring-rose-500/50 transition-all font-mono text-sm shadow-inner placeholder:text-slate-700" 
                  />
                  <button 
                    onClick={handleSpy} 
                    disabled={!url || isAnalyzing} 
                    className="px-8 py-4 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black rounded-2xl shadow-lg shadow-rose-500/20 transition-all flex items-center group active:scale-95"
                  >
                    {isAnalyzing ? <Loader2 className="animate-spin size-5" /> : <Zap size={20} className="group-hover:animate-pulse" />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 ml-1 italic leading-tight">Enter the URL of an ad you saw on Facebook, TikTok, or Taboola. Our AI will strip the cloaker and analyze the psychology.</p>
              </div>

              {error && (
                <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-start space-x-3 text-rose-400 animate-in slide-in-from-top-2">
                  <AlertCircle size={18} className="shrink-0 mt-0.5" />
                  <p className="text-xs font-bold leading-relaxed">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-slate-950/30 border border-slate-800 rounded-3xl space-y-3">
                  <ShieldAlert size={20} className="text-amber-400" />
                  <h4 className="text-xs font-black text-white uppercase tracking-wider">Stealth Mode</h4>
                  <p className="text-[10px] text-slate-500 leading-relaxed">The Ninja Spy uses rotating residential proxies to bypass competitor bot-protection and geofencing.</p>
                </div>
                <div className="p-6 bg-slate-950/30 border border-slate-800 rounded-3xl space-y-3">
                  <Search size={20} className="text-indigo-400" />
                  <h4 className="text-xs font-black text-white uppercase tracking-wider">Deep Analysis</h4>
                  <p className="text-[10px] text-slate-500 leading-relaxed">AI detects hidden pixel IDs, tracking scripts, and psychological triggers meant to manipulate users.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-8 animate-in slide-in-from-right-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-6 bg-slate-950/50 border border-slate-700/50 rounded-3xl">
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Funnel Structure</div>
                  <div className="text-lg font-black text-white uppercase">{results.funnelType}</div>
                </div>
                <div className="p-6 bg-slate-950/50 border border-slate-700/50 rounded-3xl">
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-2">Profit Potential</div>
                  <div className={`text-lg font-black uppercase ${results.estimatedConversionPotential === 'High' ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {results.estimatedConversionPotential}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center text-[10px] text-rose-400 font-black uppercase tracking-widest mb-3">
                    <Zap size={14} className="mr-2" /> The Winning Hook
                  </div>
                  <div className="p-6 bg-slate-950 border border-slate-800 rounded-3xl text-white font-bold leading-relaxed shadow-inner border-l-4 border-l-rose-500">
                    "{results.hook}"
                  </div>
                </div>

              <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center text-[10px] text-indigo-400 font-black uppercase tracking-widest mb-3">
                      <Target size={14} className="mr-2" /> Target Audience
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed bg-slate-950/30 p-4 rounded-2xl border border-slate-800">
                      {results.targetAudience}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center text-[10px] text-amber-400 font-black uppercase tracking-widest mb-3">
                      <AlertCircle size={14} className="mr-2" /> Psych Triggers
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {results.triggers.map((t, i) => (
                        <span key={i} className="px-3 py-1.5 bg-amber-500/10 text-amber-400 text-[9px] font-black uppercase tracking-widest rounded-lg border border-amber-500/20">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
              </div>

              {/* New Traffic Intelligence Section */}
              <div className="p-6 bg-slate-900 border border-slate-800 rounded-[2rem] space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-[10px] text-blue-400 font-black uppercase tracking-widest">
                    <Globe size={14} className="mr-2" /> Traffic Intelligence
                  </div>
                  <div className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[8px] font-black text-blue-400 uppercase tracking-widest">
                    SimilarWeb Data Logic
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Primary Source</div>
                    <div className="flex items-center space-x-3 text-white font-bold text-sm">
                      <div className="size-2 bg-blue-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                      <span>{results.trafficIntelligence?.primarySource || 'Native Ads'}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-4">Ad Strategy</div>
                    <p className="text-[10px] text-slate-400 leading-relaxed italic">
                      "{results.trafficIntelligence?.strategy}"
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest">Detected Pixels</div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(results.pixelForensics || {}).map(([key, detected]) => (
                        detected && (
                          <span key={key} className="px-2.5 py-1 bg-slate-950 border border-slate-800 text-[9px] font-black text-white uppercase rounded-md flex items-center">
                            <ShieldAlert size={10} className="mr-1.5 text-emerald-400" /> {key}
                          </span>
                        )
                      ))}
                      {!Object.values(results.pixelForensics || {}).some(v => v) && (
                        <span className="text-[9px] text-slate-600 italic">No pixels detected (Cloaked?)</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-4">Compliance Risk</div>
                    <div className={`text-[10px] font-black uppercase tracking-widest ${results.trafficIntelligence?.riskLevel === 'Aggressive' ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {results.trafficIntelligence?.riskLevel || 'Normal'}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center text-[10px] text-emerald-400 font-black uppercase tracking-widest mb-3">
                  <MessageCircle size={14} className="mr-2" /> Ninja Strategy: How to beat them
                </div>
                <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl text-emerald-100 text-sm italic leading-relaxed relative">
                  <div className="absolute top-4 right-4 opacity-10"><Zap size={40} className="text-emerald-400" /></div>
                  "{results.ninjaImprovement}"
                </div>
              </div>
              </div>

              <div className="pt-4 flex space-x-4">
                <button 
                  onClick={() => setResults(null)} 
                  className="flex-1 py-5 bg-slate-800 hover:bg-slate-700 text-white font-black rounded-2xl transition-all uppercase tracking-widest text-xs active:scale-95"
                >
                  Spy Another URL
                </button>
                <button 
                  onClick={() => {
                    onModel(results);
                  }} 
                  className="flex-[1.5] py-5 bg-gradient-to-tr from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white font-black rounded-2xl shadow-xl shadow-rose-500/20 transition-all flex justify-center items-center active:scale-95 uppercase tracking-widest text-sm"
                >
                  Model This Funnel <ArrowRight size={18} className="ml-2" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitionSpyModal;
